import { Tabs, TTab } from '@unmind/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from 'Account/hooks';
import { ManagerReporting } from 'Admin/Dashboard/ManagerReporting';
import { DashboardKey } from '__generated__/globalTypes';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { useUserEntitlements } from 'hooks';
import { tracking } from '../../App/Tracking';
import LoadingIndicator from '../../Shared/LoadingIndicator';
import { FilterProvider } from '../Dashboard/FilterState';
import {
  ANALYTICS_TAB_HASHES as TAB_HASHES,
  AnalyticsTabHash as TabHash,
} from '../constants';
import { AnalyticsTabType } from '../types';
import SupersetDashboard from '../Dashboard/SupersetDashboard';
import DateRangeHeading from '../Dashboard/DateRangeHeading';
import { useOrganisationEntitlements } from '../../Shared/Entitlement';
import Route404 from '../../App/Router/Route404';
import { isAdmin } from '../../utils';
import useSupersetAnalyticsDashboards from './hooks/useSupersetAnalyticsDashboards';
const trackTabEvent = (tabName: string) =>
  tracking.track(`admin-dashboard-tab-clicked`, {
    adminTabName: tabName,
  });

const DASHBOARD_HASH_TO_TYPE: { [key in TabHash]: AnalyticsTabType } = {
  [TAB_HASHES.ACTIVITY]: 'activity',
  [TAB_HASHES.WELLBEING]: 'wellbeing',
  [TAB_HASHES.MANAGER_REPORTING]: 'managerReporting',
};

const NON_SUPERSET_DASHBOARDS = [
  TAB_HASHES.ACTIVITY,
  TAB_HASHES.WELLBEING,
  TAB_HASHES.MANAGER_REPORTING,
];

const getDashboardId = (dashboardKey: string): string =>
  `${dashboardKey.toLowerCase()}_analytics_dashboard`;

const AnalyticsSuitePage = () => {
  const { t: translate } = useTranslation('analytics');

  const { loading: orgEntitlementLoading, entitlements } =
    useOrganisationEntitlements([
      { entitlement: 'standaloneIndex', default: true },
      { entitlement: 'lineManagerFeatures', default: false },
    ]);

  const { entitlements: userEntitlements, loading: userEntitlementsLoading } =
    useUserEntitlements();

  const { data: supersetDashboards, loading: supersetDashboardsLoading } =
    useSupersetAnalyticsDashboards();

  const { loading: userLoading, permissions } = useUser();

  const showEapDashboard = useFeatureFlag(
    FEATURE_FLAGS.SHOW_EAP_ANALYTICS_DASHBOARD,
  );

  if (
    orgEntitlementLoading ||
    supersetDashboardsLoading ||
    userLoading ||
    userEntitlementsLoading
  ) {
    return <LoadingIndicator />;
  }

  // Client admins with Standalone Index entitlement should not have access to admin dashboard
  // As they should not be able to invite employees to the main app.
  if (entitlements.standaloneIndex || !userEntitlements.supersetAnalytics) {
    return <Route404 />;
  }

  const userIsAdmin = isAdmin(permissions);

  const dashboards = supersetDashboards?.analytics?.dashboards || [];

  const createDashboardTab = (dashboard: {
    id: string;
    title: string;
    key: DashboardKey;
  }): TTab => ({
    text: dashboard.title,
    id: getDashboardId(dashboard.key) as TabHash,
    content: <SupersetDashboard dashboardId={dashboard.id} />,
    onClick: () =>
      trackTabEvent(`${dashboard.key.toLocaleLowerCase()}-analytics-dashboard`),
  });

  const eapDashboard = dashboards.find(
    dashboard => dashboard.key === DashboardKey.EAP,
  );

  const tabs: TTab[] = [
    ...dashboards
      .filter(dashboard => dashboard.key !== DashboardKey.EAP)
      .map(createDashboardTab),
    /**
     * The EAP dashboard is gated behind a LaunchDarkly feature flag
     */
    ...(showEapDashboard && eapDashboard
      ? [createDashboardTab(eapDashboard)]
      : []),
    /**
     * Only show manager reporting to organisations
     * with the line manager features entitlement enabled
     */
    ...(entitlements.lineManagerFeatures && userIsAdmin
      ? [
          {
            text: 'Manager Reporting',
            id: 'manager_reporting',
            content: <ManagerReporting />,
            onClick: () => trackTabEvent('manager-reporting'),
          },
        ]
      : []),
  ];

  const activeTab = window.location.hash.replace('#', '') as TabHash;
  const isInsightsTabSelected = NON_SUPERSET_DASHBOARDS.includes(activeTab);

  return (
    <FilterProvider tabType={DASHBOARD_HASH_TO_TYPE[activeTab]}>
      <Tabs className="analytics-suite" tabs={tabs}>
        <h1 className="primary-title">{translate('title')}</h1>
        {isInsightsTabSelected ? <DateRangeHeading /> : null}
      </Tabs>
    </FilterProvider>
  );
};

export default AnalyticsSuitePage;
